import { useEffect, useRef, useState } from "react";
import { Film } from "lucide-react";
import { api } from "@/lib/api";

function ReelCard({ reel }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = Number(reel.playback_speed) || 1.5;
  }, [reel.playback_speed]);

  return (
    <article className="min-w-[260px] sm:min-w-[300px] lg:min-w-0 border border-[#F4F4F0]/25 bg-black overflow-hidden">
      <div className="aspect-[9/16] bg-[#111] relative overflow-hidden">
        <video
          ref={videoRef}
          src={reel.video?.url}
          poster={reel.poster?.url || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          onLoadedMetadata={(e) => { e.currentTarget.playbackRate = Number(reel.playback_speed) || 1.5; }}
        />
      </div>
      <div className="p-4 text-[#F4F4F0]">
        <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/55">{reel.category}</div>
        <h3 className="font-display text-2xl mt-1 leading-tight">{reel.title}</h3>
        {reel.caption ? <p className="text-sm mt-2 text-white/65 leading-relaxed">{reel.caption}</p> : null}
      </div>
    </article>
  );
}

export default function ReelsShowcase() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reels", { params: { limit: 5 } })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="reels" className="relative bg-[#0A0A0A] text-[#F4F4F0] border-t border-[#F4F4F0]/20 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 mb-12">
          <div className="md:col-span-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-[#F4F4F0]" />
              <span className="font-mono tracking-[0.22em] uppercase">Made in Motion</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
              See the press <em className="italic">in motion.</em>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-[52ch]">
              Short glimpses of printing, finishing and finished products from the Arvind Printers floor.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="border border-dashed border-white/25 min-h-[300px] grid place-items-center text-center px-6">
            <div className="font-mono tracking-[0.18em] uppercase text-white/45">Loading reels…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-white/25 min-h-[360px] grid place-items-center text-center px-6 bg-white/[0.02]">
            <div>
              <Film className="w-9 h-9 mx-auto mb-4 opacity-50" />
              <div className="font-display text-3xl md:text-4xl">Production reels are coming to the floor.</div>
              <p className="mt-3 text-white/55 max-w-xl mx-auto">
                We are preparing short behind-the-scenes videos of our presses, finishing processes and product work.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex lg:grid lg:grid-cols-5 gap-4 overflow-x-auto pb-3 snap-x snap-mandatory">
            {items.map((reel) => (
              <div key={reel.id} className="snap-start"><ReelCard reel={reel} /></div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
